import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dadqerbep053ijub142g";

type Status = "idle" | "submitting" | "success" | "error";

export default function Subscribe() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("contact_alt") || "").toString().trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      if (!value) return;
      if (key === "contact_alt") return;
      params.append(key, value.toString());
    });

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* 保留原始文本 */
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const lower = serverMsg.toString().toLowerCase();

      if (!res.ok || parsed?.code !== "OK" || lower.includes("spam")) {
        setStatus("error");
        setFormError(
          serverMsg && !lower.includes("spam") && !lower.includes("<!doctype")
            ? serverMsg.toString()
            : "提交失败，请检查信息后重试。"
        );
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后重试。");
    }
  }

  return (
    <section id="subscribe" className="container-px py-16 md:py-24">
      <div className="relative overflow-hidden rounded-3xl bg-primary-500 px-5 py-12 md:px-14 md:py-16">
        <div
          className="pointer-events-none absolute -left-10 top-0 h-48 w-48 rounded-full bg-background-50/10 blur-2xl"
          aria-hidden="true"
        ></div>
        <div
          className="pointer-events-none absolute -bottom-16 right-8 h-56 w-56 rounded-full bg-accent-600/30 blur-2xl"
          aria-hidden="true"
        ></div>

        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
              现在就订阅，别错过下一次更新
            </h2>
            <p className="mt-4 text-sm leading-7 text-white/85 md:text-base">
              留下你的邮箱并选择关注方向，开启推送通知后，新料一更新就第一时间提醒你。
              无需反复刷新，订阅提醒从此替你盯着最新动态。
            </p>
            <ul className="mt-6 hidden flex-col gap-2 text-sm text-background-50 lg:flex">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" aria-hidden="true"></i>
                免费订阅，随时可取消
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" aria-hidden="true"></i>
                第一时间的推送通知
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-200" aria-hidden="true"></i>
                智能去重，推送不扰人
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-background-50 p-6 md:p-7">
            {status === "success" ? (
              <div className="flex flex-col items-center py-8 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-check-line text-3xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                  订阅请求已收到
                </h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">
                  请前往邮箱完成确认，确认后即可第一时间收到新料推送与更新提醒。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 whitespace-nowrap rounded-full border border-background-200 px-5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100"
                >
                  再订阅一个邮箱
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-4">
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  订阅新料推送
                </h3>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">邮箱地址</span>
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="name@example.com"
                    className="rounded-lg border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">关注方向</span>
                  <select
                    name="focus"
                    className="rounded-lg border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="最新动态">最新动态</option>
                    <option value="作者追更">作者追更</option>
                    <option value="限量上新">限量上新</option>
                    <option value="行业资讯">行业资讯</option>
                    <option value="关键词话题">关键词话题</option>
                  </select>
                </label>

                {/* 反垃圾隐藏域 */}
                <input
                  type="text"
                  name="contact_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="hp-guard"
                />

                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-notification-badge-line text-lg" aria-hidden="true"></i>
                      立即订阅提醒
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-foreground-500">
                  点击订阅即表示同意接收更新提醒，可随时退订。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}